import {
  resolveWizardChannelPrimaryMedia,
  setWizardChannelPrimaryMedia,
  type WizardMediaOption,
} from "@/lib/wizard-channel-media";
import type { WizardChannelIntent } from "@/lib/wizard-selling";

const selectCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";

/**
 * One account's primary image, chosen from the shared product image library.
 * Nothing here duplicates or edits an image, and the Product Master primary
 * image is never changed by this selection.
 */
export default function ChannelPrimaryImagePicker(props: {
  intent: WizardChannelIntent;
  intents: WizardChannelIntent[];
  media: WizardMediaOption[];
  onIntents: (next: WizardChannelIntent[]) => void;
}) {
  const resolution = resolveWizardChannelPrimaryMedia({
    chosenMediaAssetId: props.intent.primaryMediaAssetId,
    gallery: props.media,
  });
  const chosenSrc = resolution.mediaAssetId
    ? props.media.find((item) => item.mediaAssetId === resolution.mediaAssetId)?.src ?? null
    : null;

  if (props.media.length === 0) {
    return (
      <div className="mt-3 ml-7 text-xs text-slate-500" data-testid={`channel-primary-image-${props.intent.connectorId}`}>
        {props.intent.primaryMediaAssetId
          ? resolution.label
          : "No product images yet. Add images on the Product step to choose a channel-specific primary image."}
      </div>
    );
  }

  return (
    <div className="mt-3 ml-7 space-y-2" data-testid={`channel-primary-image-${props.intent.connectorId}`}>
      <label className="block text-xs font-medium text-slate-500" htmlFor={`channel-primary-${props.intent.connectorId}`}>
        Primary image for {props.intent.displayName}
      </label>
      <div className="flex items-start gap-2">
        <div className="w-12 h-12 shrink-0 rounded-md bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center">
          {chosenSrc ? (
            <img src={chosenSrc} alt={resolution.filename ?? "Channel primary image"} className="w-full h-full object-cover" />
          ) : (
            <span className="w-5 h-5 flex items-center justify-center text-slate-400">
              <i className="ri-image-line" />
            </span>
          )}
        </div>
        <select
          id={`channel-primary-${props.intent.connectorId}`}
          aria-label={`${props.intent.displayName} primary image`}
          value={props.intent.primaryMediaAssetId ?? ""}
          onChange={(e) => props.onIntents(setWizardChannelPrimaryMedia(props.intents, props.intent.connectorId, e.target.value || null))}
          className={selectCls}
        >
          <option value="">Use the Product Master primary image</option>
          {props.media.map((item) => (
            <option key={item.mediaAssetId} value={item.mediaAssetId}>
              {item.filename ?? "Image"}{item.isPrimary ? " (Product Master primary)" : ""}
            </option>
          ))}
        </select>
      </div>
      <p className={`text-xs ${resolution.authority === "missing_override" ? "text-amber-700" : "text-slate-500"}`}>
        {resolution.label}
      </p>
      <p className="text-[11px] text-slate-400">
        Affects this account only. The shared catalogue image library and its primary image are unchanged.
      </p>
    </div>
  );
}